/**
 * Where to look for the phone, and what to make of what was found.
 *
 * Pure functions over boxes and landmarks, so every rule here is testable
 * without a browser or a model. The rules come from measuring a stock detector
 * on real mirror selfies:
 *
 *  - Run over the whole frame, it finds the phone in close shots and misses it
 *    in full-length ones, where the phone is ten pixels tall by the time the
 *    frame is shrunk to the detector's input.
 *  - Run over a crop around each hand, it found the phone in nine of eleven,
 *    at 0.67–0.87 confidence. The phone is always in a hand, and the pose model
 *    finds hands at sizes the detector cannot.
 *
 * So the hands decide where to look, and the whole-frame pass is kept as a
 * second opinion for when the pose model attaches to the wrong person.
 */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** The subset of a pose result this module needs, in pixels. */
export interface Body {
  /** Wrist-to-knuckle midpoints, the centre of a gripping hand. */
  hands: Point[];
  /** Nose, eyes, ears and mouth. */
  face: Point[];
  shoulderWidth: number;
}

export interface Candidate {
  box: Box;
  score: number;
}

export interface Phone extends Candidate {
  /** A hand is on it — as opposed to standing on a shelf or the floor. */
  held: boolean;
  /** Share of the face it hides, 0–1. */
  coversFace: number;
}

/** MediaPipe pose indices. */
export const POSE = {
  nose: 0,
  faceFirst: 0,
  faceLast: 10,
  leftShoulder: 11,
  rightShoulder: 12,
  leftWrist: 15,
  rightWrist: 16,
  leftIndex: 19,
  rightIndex: 20,
} as const;

interface Landmark {
  x: number;
  y: number;
  visibility?: number;
}

/** A wrist above the nose: in hands-free mode, the signal to start the timer. */
export function handRaised(landmarks: Landmark[]): boolean {
  const nose = landmarks[POSE.nose];
  return [POSE.leftWrist, POSE.rightWrist].some(
    (i) => (landmarks[i].visibility ?? 1) > 0.5 && landmarks[i].y < nose.y,
  );
}

/** Convert one normalised pose to pixels, keeping only what is visible. */
export function bodyFrom(landmarks: Landmark[], w: number, h: number): Body {
  const at = (i: number): Point => ({ x: landmarks[i].x * w, y: landmarks[i].y * h });
  const seen = (i: number) => (landmarks[i].visibility ?? 1) > 0.3;

  const hands: Point[] = [];
  for (const [wrist, index] of [
    [POSE.leftWrist, POSE.leftIndex],
    [POSE.rightWrist, POSE.rightIndex],
  ] as const) {
    if (!seen(wrist)) continue;
    const a = at(wrist);
    const b = at(index);
    hands.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  }

  // Face points are kept even when the model marks them unseen. A face hidden
  // behind a phone is exactly the case that matters, and the pose model still
  // places the points it cannot see from the shoulders and head outline —
  // dropping them is how a phone over the face used to get erased anyway.
  const face: Point[] = [];
  for (let i = POSE.faceFirst; i <= POSE.faceLast; i++) face.push(at(i));

  const shoulderWidth = Math.abs(at(POSE.leftShoulder).x - at(POSE.rightShoulder).x);
  return { hands, face, shoulderWidth };
}

/**
 * Crops to run the detector over, in pixels, clamped to the frame.
 *
 * One crop per hand, sized from the shoulders, sitting mostly above the grip
 * because that is where a held phone stands. Hands close together — the
 * two-handed chest hold, which is also the pose that erases best — share one
 * crop between them. Each crop costs a detector pass (about half a second on a
 * phone's CPU), so the count is kept to what the anatomy actually needs: one
 * or two, where a crop per hand per scale used to mean six.
 */
export function searchCrops(body: Body, w: number, h: number): Box[] {
  const unit = Math.max(48, body.shoulderWidth);
  let anchors = [...body.hands];
  if (anchors.length === 2) {
    const [a, b] = anchors;
    if (Math.hypot(a.x - b.x, a.y - b.y) < unit * 1.2) anchors = [{ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }];
  }

  const crops: Box[] = [];
  for (const p of anchors) {
    const side = unit * 1.5;
    const x0 = Math.max(0, p.x - side / 2);
    const y0 = Math.max(0, p.y - side * 0.65);
    const x1 = Math.min(w, x0 + side);
    const y1 = Math.min(h, y0 + side);
    if (x1 - x0 >= 24 && y1 - y0 >= 24) crops.push({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
  }
  return crops;
}

export function area(b: Box): number {
  return Math.max(0, b.w) * Math.max(0, b.h);
}

export function intersection(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

export function iou(a: Box, b: Box): number {
  const i = intersection(a, b);
  return i > 0 ? i / (area(a) + area(b) - i) : 0;
}

function union(a: Box, b: Box): Box {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/**
 * One phone per cluster of overlapping detections.
 *
 * The same phone is found several times — once per crop that contains it, and
 * sometimes in pieces, when fingers split it into a top half and a bottom half
 * that the detector boxes separately. Overlapping boxes are therefore merged
 * into their union rather than thinned to the best one: the union is the more
 * complete phone, and a complete mask is what keeps the inpainter from growing
 * the missing half back.
 */
export function suppress(found: Candidate[]): Candidate[] {
  const sorted = [...found].sort((a, b) => b.score - a.score);
  const kept: Candidate[] = [];
  for (const c of sorted) {
    const k = kept.find((q) => intersection(q.box, c.box) / Math.min(area(q.box), area(c.box)) > 0.15);
    if (!k) {
      kept.push({ box: { ...c.box }, score: c.score });
      continue;
    }
    // Only merge pieces the detector believes in: a faint box overlapping a
    // confident one is more often a hand or a sleeve than more of the phone.
    if (c.score >= k.score * 0.5) k.box = union(k.box, c.box);
  }
  return kept;
}

/**
 * The face, as a box, from the pose's face points.
 *
 * Those points run from the eyebrows to the mouth, so the box is grown upward
 * for the forehead and downward for the chin — a phone covering only the chin
 * still covers the face.
 */
export function faceBox(body: Body): Box | null {
  if (body.face.length < 3) return null;
  const xs = body.face.map((p) => p.x);
  const ys = body.face.map((p) => p.y);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const span = Math.max(x1 - x0, body.shoulderWidth * 0.35, 8);
  return { x: (x0 + x1) / 2 - span * 0.6, y: y0 - span * 0.55, w: span * 1.2, h: y1 - y0 + span * 1.05 };
}

/**
 * Is a hand on this phone? Generous: a grip can sit at any edge of it, and in
 * the two-handed chest hold the hand points the pose model reports sit well
 * below the phone they are holding.
 */
export function isHeld(phone: Box, hands: Point[]): boolean {
  const reach = Math.max(phone.w, phone.h) * 1.25;
  return hands.some((p) => {
    const dx = Math.max(phone.x - p.x, 0, p.x - (phone.x + phone.w));
    const dy = Math.max(phone.y - p.y, 0, p.y - (phone.y + phone.h));
    return Math.hypot(dx, dy) <= reach;
  });
}

/**
 * Minimum detector scores. A phone in a hand has the hand to vouch for it; a
 * phone on its own — on a shelf, on the floor — has to convince the detector
 * alone, and a dark picture frame is phone-shaped enough to fool it at 0.3.
 */
/** Above this share of the face hidden, the face is too hidden to restore honestly. */
export const FACE_LIMIT = 0.2;

export const HELD_FLOOR = 0.25;
export const LOOSE_FLOOR = 0.45;
/**
 * A phone over the face is held to a much lower bar, because the only thing
 * done with one is to refuse to erase it and say so. It is also exactly where
 * the detector is weakest — a face and a phone overlapping is not a scene COCO
 * has much of — and a missed one meant the coach stayed quiet while somebody
 * took the one photograph that cannot be fixed. A false alarm here costs a
 * suggestion to lower the phone; nothing is erased on its strength.
 */
export const FACE_FLOOR = 0.1;

export function classify(found: Candidate[], bodies: Body[]): Phone[] {
  const hands = bodies.flatMap((b) => b.hands);
  const faces = bodies.map(faceBox).filter((f): f is Box => f !== null);
  const phones: Phone[] = [];
  for (const c of suppress(found)) {
    const held = isHeld(c.box, hands);
    let covers = 0;
    for (const f of faces) covers = Math.max(covers, intersection(c.box, f) / Math.max(1, area(f)));
    const floor = covers > FACE_LIMIT && held ? FACE_FLOOR : held ? HELD_FLOOR : LOOSE_FLOOR;
    if (c.score < floor) continue;
    phones.push({ ...c, held, coversFace: covers });
  }
  return phones;
}

